import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import safeStorage from '../../utils/safeStorage';
import ComponentList from './ComponentList';
import ComponentForm from './ComponentForm';
import axios from 'axios';

// API URL configuration - uses relative URL in production (nginx proxies to backend)
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use(config => {
  const token = safeStorage.getItem('authToken');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});
const manufacturerApi = axios.create({ baseURL: API_URL });
const categoryApi = axios.create({ baseURL: API_URL });
const subcategoryApi = axios.create({ baseURL: API_URL });

const ComponentScreen = () => {
  const { isReadOnly } = useAuth();
  const [components, setComponents] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [editingComponent, setEditingComponent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({
    component_name: '',
    manufacturer_id: '',
    component_category_id: '',
    component_subcategory_id: '',
    component_description: ''
  });

  const readOnlyMode = isReadOnly('/component_screen');

  // Fetch all manufacturers and categories on mount
  useEffect(() => {
    manufacturerApi.get('/manufacturers').then(res => setManufacturers(res.data));
    categoryApi.get('/component_categories').then(res => setCategories(res.data));
  }, []);

  // Fetch subcategories when category filter changes
  useEffect(() => {
    if (filters.component_category_id) {
      subcategoryApi
        .get('/component_subcategories', { params: { component_category_id: filters.component_category_id } })
        .then(res => setSubcategories(res.data));
    } else {
      setSubcategories([]);
    }
    // Reset subcategory filter if category changes
    setFilters(f => ({ ...f, component_subcategory_id: '' }));
    // eslint-disable-next-line
  }, [filters.component_category_id]);

  // Fetch components when filters change
  useEffect(() => {
    const fetchComponents = async () => {
      const params = {};
      Object.keys(filters).forEach(key => {
        if (filters[key]) params[key] = filters[key];
      });
      const res = await api.get('/components', { params });
      setComponents(res.data);
    };
    fetchComponents();
    // eslint-disable-next-line
  }, [filters]);

  const handleCreate = () => {
    if (readOnlyMode) return;
    setEditingComponent(null);
    setShowForm(true);
  };

  const handleEdit = (component) => {
    if (readOnlyMode) return;
    setEditingComponent(component);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (readOnlyMode) return;
    await api.delete(`/components/${id}`);
    // Refresh list after delete
    const params = {};
    Object.keys(filters).forEach(key => {
      if (filters[key]) params[key] = filters[key];
    });
    const res = await api.get('/components', { params });
    setComponents(res.data);
  };

  const handleSave = async (formData) => {
    if (editingComponent) {
      await api.put(`/components/${editingComponent.component_id}`, formData);
    } else {
      await api.post('/components', formData);
    }
    // Refresh list after save
    const params = {};
    Object.keys(filters).forEach(key => {
      if (filters[key]) params[key] = filters[key];
    });
    const res = await api.get('/components', { params });
    setComponents(res.data);
    setShowForm(false);
    setEditingComponent(null);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingComponent(null);
  };

  const handleFilterChange = (field, value) => {
    setFilters(f => ({ ...f, [field]: value }));
  };

  return (
    <div>
      <h1>Components</h1>
      {!readOnlyMode && (
        <button onClick={handleCreate}>Create New Component</button>
      )}
      {readOnlyMode && (
        <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '1rem' }}>
          Read-only mode: You can view but not modify components
        </p>
      )}
      {showForm && !readOnlyMode && (
        <ComponentForm
          component={editingComponent}
          manufacturers={manufacturers}
          categories={categories}
          subcategories={subcategories}
          onSave={handleSave}
          onCancel={handleCancel}
          onCategoryChange={catId => setFilters(f => ({ ...f, component_category_id: catId }))}
        />
      )}
      <ComponentList
        components={components}
        filters={filters}
        onFilterChange={handleFilterChange}
        manufacturers={manufacturers}
        categories={categories}
        subcategories={subcategories}
        onEdit={readOnlyMode ? null : handleEdit}
        onDelete={readOnlyMode ? null : handleDelete}
        readOnly={readOnlyMode}
      />
    </div>
  );
};

export default ComponentScreen;